import {
  emptyUseQuery,
  patientTab,
  ProfileItem,
  ProfileVersion,
  SubTab,
  SubTabGroup,
} from '@hb/shared'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { useUserFromId } from '../../hooks/backend/user/useUserFromId'
import { emptyPopulatedAssessmentInterface } from '../assessments/AssessmentContext'
import {
  ProfileContextData,
  ProfileEntity,
  ProfileGoTo,
  ProfileNav,
  ProfilePathData,
} from './types'

export const ProfileContext = createContext<ProfileContextData>({
  user: null,
  adminItem: null,
  isInvite: false,
  practiceId: null,
  profileVersion: 'v1',
  userId: null,
  viewedTemplate: null,
  adminPatientRef: null,
  baseItemType: null,
  baseItem: null,
  selectedItem: null,
  subTabs: [],
  loading: false,
  consentForms: {
    data: null,
    dataArr: [],
    error: null,
    loading: false,
  },
  patientRef: null,
  emailVerification: { verified: false, loading: false, refetch: () => {} },
  admin: false,
  setViewedTemplateKey: () => {},
  assessments: null,
  officeVisits: emptyUseQuery,
  claims: emptyUseQuery,
  encounters: emptyUseQuery,
  selectedAssessment: emptyPopulatedAssessmentInterface,
  mostRecentPregnancy: null,
  assessmentId: null,
  practicePatientRef: null,
  tab: patientTab,
  pregnancyCorrections: {},
  pregnancyData: {},
  subTab: null,
  getTabPath: () => '',
  goTo: () => {},
  tabs: [],
})

const baseItemPaths: Record<ProfileItem['type'], string> = {
  patient: 'patients',
  pregnancy: 'pregnancies',
  claim: 'claims',
  // visit: 'visits',
}
const getBaseItemPath = (item: ProfileItem) => `${baseItemPaths[item.type]}/${item.id}`

const getEntityPath = (entity: ProfileEntity) => {
  if (entity.type === 'patient') return 'patient'
  return `${baseItemPaths[entity.type]}/${entity.id}`
}

export interface ResolvedParams {
  baseItem: ProfileItem | null
  baseItemType: string | null
  selectedItem: ProfileItem | null
  tabs: Array<SubTab | SubTabGroup>
  tab: SubTab | SubTabGroup | null
  subTabs: Array<SubTab | SubTabGroup>
  subTab: SubTab | null
  userId: string | null
}

export type NavigateFunction = (path: string) => void

const useProfileNav = (
  profileVersion: ProfileVersion,
  basePath: string,
  resolvedParams: ResolvedParams,
  navigate: NavigateFunction,
): ProfileNav => {
  const { baseItem, selectedItem, tab, subTab, tabs, userId, subTabs, baseItemType } =
    resolvedParams

  const getTabPath = useCallback(
    ({ entity, tab }: ProfilePathData) => {
      if (!baseItem) return basePath
      const isBaseItem =
        !entity ||
        (!!baseItem &&
          entity.type === 'pregnancy' &&
          baseItem.type === 'pregnancy' &&
          entity.id === baseItem.id)

      const baseItemPath = getBaseItemPath(baseItem)
      const entityPath = isBaseItem ? baseItemPath : `${baseItemPath}/${getEntityPath(entity)}`
      const realBasePath = basePath === '/' ? '' : basePath
      if (!tab) return `${realBasePath}/${entityPath}`
      return `${realBasePath}/${entityPath}/${tab}`
    },
    [basePath, baseItem],
  )

  const goTo = useCallback<ProfileGoTo>(
    data => {
      const tabPath = getTabPath(data)
      return navigate(tabPath)
    },
    [getTabPath, navigate],
  )

  return {
    profileVersion,
    getTabPath,
    baseItemType,
    userId,
    subTabs,
    baseItem,
    selectedItem,
    goTo,
    tab,
    tabs,
    subTab,
  }
}

export interface UseProfileArgs {
  basePath: string
  profileVersion: 'v1' | 'v2'
  resolvedParams: ResolvedParams
  navigate: NavigateFunction
}

export const useProfileData = (args: UseProfileArgs): ProfileContextData => {
  const { basePath, profileVersion, resolvedParams, navigate } = args

  const navData = useProfileNav(profileVersion, basePath, resolvedParams, navigate)
  const { selectedItem, userId } = navData
  const userData = useUserFromId(true, userId, selectedItem)

  return useMemo(
    () => ({
      ...userData,
      ...navData,
    }),
    [userData, navData],
  )
}

export const useProfile = () => useContext(ProfileContext)
